import { casOpsApi, CollectionModel, QueryTag, TagList } from '../api';
import { ModelWithId } from './commonModels';
import { ConfigurationGroupDeviceType } from './commonModels';

export enum ConfigPackageFileType {
  Library = 'Library',
  Settings = 'Settings',
  Custom = 'Custom',
}

export interface ConfigurationGroupPackageModel extends ModelWithId {
  name: string; // max 20 characters
  ver: string; // version
  ty: ConfigPackageFileType;
  at: string; // activation time
  uc: boolean; // upload complete
}

export interface ConfigurationGroupBasicModel extends ModelWithId {
  name: string; // Max 128 chars with regex "^[a-zA-Z0-9-_\\.\\s]+$"
  desc: string; // description. Max 255 chars
  hw: ConfigurationGroupDeviceType;
  def: boolean; // Configuration group is default
  used: boolean; // disable delete (because there are assets assigned)
  totA: number; // total numer of assets associated with this configuration group
  packages: ConfigurationGroupPackageModel[];
}

export interface ConfigurationGroupListModel
  extends Omit<ConfigurationGroupBasicModel, 'packages'> {}

export interface ConfigurationGroupModel extends ConfigurationGroupBasicModel {}

export interface CreateConfigurationGroupModel
  extends Pick<ConfigurationGroupBasicModel, 'name' | 'desc' | 'hw'> {}

export interface CreateConfigurationGroupPackageFormSettingsModel
  extends Pick<ConfigurationGroupPackageModel, 'ty' | 'name' | 'at'> {}

export interface CreateConfigurationGroupPackageFormLibraryModel
  extends Pick<ConfigurationGroupPackageModel, 'ver' | 'name' | 'ty' | 'at'> {}

export interface CreateConfigurationGroupPackageFormCustomModel
  extends Pick<ConfigurationGroupPackageModel, 'ver' | 'name' | 'ty' | 'at'> {}

export interface EditAssetsAssignConfigurationGroupFormModel {
  name: number | null;
}

export interface EditAssetsAssignConfigurationGroupModel {
  assetIds: number[];
}

// Cannot modify ConfigurationGroupDeviceType - there are too many problems in allowing this
export interface EditConfigurationGroupModel
  extends Pick<ConfigurationGroupBasicModel, 'name' | 'desc'> {}

export type ConfigurationGroupPackageFormModel = (
  | Omit<CreateConfigurationGroupPackageFormSettingsModel, 'ty'>
  | Omit<CreateConfigurationGroupPackageFormLibraryModel, 'ty'>
  | Omit<CreateConfigurationGroupPackageFormCustomModel, 'ty'>
) & {
  ty: ConfigPackageFileType | null;
};

export interface ConfigurationGroupFormModel
  extends CreateConfigurationGroupModel,
    EditConfigurationGroupModel {}

export interface ConfigPackageRequest extends ModelWithId {
  configGroupId: number;
}

export const configurationGroupsApi = casOpsApi.injectEndpoints({
  endpoints: build => ({
    listConfigurationGroups: build.query<ConfigurationGroupBasicModel[], void>({
      query: () => 'api/configurationgroups',
      transformResponse: (response: CollectionModel<ConfigurationGroupBasicModel>, meta, arg) =>
        response.items,
      providesTags: (result, error, arg) =>
        result ? [{ type: QueryTag.ConfigurationGroup, id: TagList }] : [],
    }),
    getConfigurationGroup: build.query<ConfigurationGroupModel, ModelWithId>({
      query: ({ id }) => `api/configurationgroups/${id}`,
      providesTags: (result, error, { id }) =>
        result ? [{ type: QueryTag.ConfigurationGroup, id: id }] : [],
    }),
    createConfigurationGroup: build.mutation<void, CreateConfigurationGroupModel>({
      query: body => ({
        url: 'api/configurationgroups',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: QueryTag.ConfigurationGroup, id: TagList }],
    }),
    editConfigurationGroup: build.mutation<void, EditConfigurationGroupModel & ModelWithId>({
      query: ({ id, ...body }) => ({
        url: `api/configurationgroups/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
              { type: QueryTag.ConfigurationGroup, id: TagList },
              { type: QueryTag.ConfigurationGroup, id: id },
            ]
          : [],
    }),
    editAssetsAssignConfigurationGroup: build.mutation<
      void,
      EditAssetsAssignConfigurationGroupModel & ModelWithId
    >({
      query: ({ id, assetIds }) => ({
        url: `api/configurationgroups/${id}/assignassets`,
        method: 'POST',
        body: { assetIds },
      }),
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
              { type: QueryTag.ConfigurationGroup, id: TagList },
              { type: QueryTag.ConfigurationGroup, id: id },
              { type: QueryTag.AssetRegistered, id: TagList },
            ]
          : [],
    }),
    deleteConfigurationGroup: build.mutation<void, ModelWithId>({
      query: ({ id }) => ({
        url: `api/configurationgroups/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
              { type: QueryTag.ConfigurationGroup, id: TagList },
              { type: QueryTag.ConfigurationGroup, id: id },
            ]
          : [],
    }),
    deleteConfigurationGroupPackage: build.mutation<void, ConfigPackageRequest>({
      query: ({ configGroupId, id }) => ({
        url: `api/configurationgroups/${configGroupId}/packages/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { configGroupId }) =>
        !error ? [{ type: QueryTag.ConfigurationGroup, id: configGroupId }] : [],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListConfigurationGroupsQuery,
  useGetConfigurationGroupQuery,
  useCreateConfigurationGroupMutation,
  useEditConfigurationGroupMutation,
  useEditAssetsAssignConfigurationGroupMutation,
  useDeleteConfigurationGroupMutation,
  useDeleteConfigurationGroupPackageMutation,
} = configurationGroupsApi;
